import { useCallback, useEffect, useState } from 'react';
import { getErrorMessage } from '../api/client';
import { discardSubmission, getAvailableForms, listSubmissions, startSubmission } from '../api/formSubmissions';
import { getStores } from '../api/stores';
import { useAuth } from '../auth/AuthProvider';
import { FormRunner } from '../components/FormRunner';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { PageHeader } from '../components/ui/PageHeader';
import { Button, Field, LoadingPanel, Select } from '../components/ui/primitives';
import { DenseList, DenseRow } from '../components/ui/DenseList';
import { useShellTier } from '../layouts/shellTier';
import type { AvailableForm, SubmissionListItem } from '../types/formSubmissions';
import type { Store } from '../types/stores';
import { DEFAULT_STORE_TIMEZONE } from '../utils/timezones';

// Fase 23B: pantalla OPERACIONAL "Formularios" (ejecucion). Distinta de "Formularios y checklists"
// (configuracion / 23A). Lista los formularios que el usuario puede responder en su tienda + sus
// envios recientes, y abre el runner.
export function OperationalFormsPage() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const isEmployee = user?.role === 'EMPLOYEE';
  const [stores, setStores] = useState<Store[]>([]);
  const [storeId, setStoreId] = useState<string>('');
  const [forms, setForms] = useState<AvailableForm[]>([]);
  const [recent, setRecent] = useState<SubmissionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const tier = useShellTier();
  const [error, setError] = useState<string>();
  const [openId, setOpenId] = useState<string | null>(null);
  const [busyTemplate, setBusyTemplate] = useState<string>();

  // P-075: con una sola tienda no hay nada que elegir: se elige sola. Con varias, «Responder» espera a que se
  // elija una (el servidor necesita la tienda para empezar: con «Todas mis tiendas» respondía 400).
  useEffect(() => { if (!isEmployee) void getStores().then((r) => { setStores(r.stores); if (r.stores.length === 1) setStoreId(r.stores[0]!.id); }).catch(() => {}); }, [isEmployee]);
  const needsStore = !isEmployee && stores.length > 1 && !storeId;

  const load = useCallback(async () => {
    setLoading(true); setError(undefined);
    try {
      const [a, r] = await Promise.all([getAvailableForms(storeId || undefined), listSubmissions({ mine: true })]);
      setForms(a.forms);
      setRecent(r.submissions);
    } catch (e) { setError(getErrorMessage(e, 'No se pudieron cargar los formularios.')); }
    finally { setLoading(false); }
  }, [storeId]);

  useEffect(() => { void load(); }, [load]);

  const respond = async (templateId: string) => {
    setBusyTemplate(templateId); setError(undefined);
    try {
      const res = await startSubmission({ templateId, storeId: isEmployee ? undefined : (storeId || undefined), context: 'MANUAL' });
      setOpenId(res.submission.id);
    } catch (e) { setError(getErrorMessage(e, 'No se pudo iniciar el formulario.')); }
    finally { setBusyTemplate(undefined); }
  };

  // Descartar un borrador propio (p. ej. una respuesta MANUAL vieja de un formulario del corte). El
  // backend solo permite descartar borradores propios no ligados a un corte.
  const discard = async (id: string) => {
    if (!(await confirm({ title: 'Descartar borrador', description: '¿Descartar este borrador? Esta acción no se puede deshacer.', confirmLabel: 'Descartar', danger: true }))) return;
    setError(undefined);
    try { await discardSubmission(id); await load(); }
    catch (e) { setError(getErrorMessage(e, 'No se pudo descartar el borrador.')); }
  };

  if (openId) return <FormRunner submissionId={openId} onBack={() => { setOpenId(null); void load(); }} />;

  return (
    <section aria-label="Formularios" className="n3-screen">
      <PageHeader description="Responde los formularios y checklists asignados a tu tienda. Los del corte se responden desde Cortes de turno." title="Formularios" />
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {!isEmployee && stores.length > 1 && (
        <Field hint={needsStore ? 'Elige la tienda en la que vas a responder.' : undefined} label="Tienda">
          <Select onChange={(e) => setStoreId(e.target.value)} value={storeId}>
            <option value="">Todas mis tiendas</option>
            {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
        </Field>
      )}

      {loading ? <LoadingPanel label="Cargando formularios…" /> : (
        <>
          <h2 className="form-op__subtitle">Disponibles</h2>
          {forms.length === 0 ? <div className="empty-panel"><strong>Sin formularios disponibles</strong><span>No hay formularios publicados para tu tienda.</span></div> : (
            <div className="form-op__grid">
              {forms.map((f) => {
                const draft = recent.find((r) => r.templateId === f.templateId && r.status === 'DRAFT');
                return (
                  <div key={f.templateId} className="form-op__card">
                    <div className="form-op__card-head"><strong>{f.name}</strong>{f.shiftClose && <span className={`form-badge ${f.shiftClose.requirement === 'REQUIRED' ? 'form-badge--draft' : 'form-badge--inactive'}`}>{f.shiftClose.requirement === 'REQUIRED' ? 'Corte: obligatorio' : 'Corte: opcional'}</span>}</div>
                    {f.description && <p className="form-op__desc">{f.description}</p>}
                    <small>{f.activeVersion?.questionCount ?? 0} pregunta(s)</small>
                    {/* Fix Corte: un formulario del corte se responde SOLO desde el corte (contexto SHIFT_CLOSE).
                        Aqui se muestra marcado, pero sin boton de responder manual (evita respuestas MANUAL que
                        no cuentan para el corte y confunden). El backend distingue por binding (f.shiftClose). */}
                    {f.shiftClose
                      ? <p className="form-op__corte-note" role="note">Se responde desde <strong>Cortes de turno</strong> (en el checklist del turno), no aquí.</p>
                      : <button className="button button--primary button--small" disabled={busyTemplate === f.templateId || needsStore} onClick={() => void respond(f.templateId)} title={needsStore ? 'Primero elige la tienda' : undefined} type="button">{draft ? 'Continuar' : 'Responder'}</button>}
                  </div>
                );
              })}
            </div>
          )}

          <h2 className="form-op__subtitle">Mis envíos recientes</h2>
          {recent.length === 0 ? <p className="form-op__desc">Aún no tienes envíos.</p> : tier === 'compact' ? (
            // P-076: en el teléfono, una fila compacta por envío (formulario, tienda, fecha, estado y acciones a la vista).
            <DenseList label="Mis envíos recientes" separated>
              {recent.map((r) => (
                <DenseRow
                  actions={<>
                    <Button onClick={() => setOpenId(r.id)} variant="ghost">{r.status === 'SUBMITTED' ? 'Ver' : 'Continuar'}</Button>
                    {r.status !== 'SUBMITTED' && <Button onClick={() => void discard(r.id)} variant="danger">Descartar</Button>}
                  </>}
                  badge={<span className={`form-badge ${r.status === 'SUBMITTED' ? 'form-badge--active' : 'form-badge--draft'}`}>{r.status === 'SUBMITTED' ? 'Enviado' : 'Borrador'}</span>}
                  key={r.id}
                  meta={[r.storeName, new Date(r.submittedAt ?? r.startedAt).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short', timeZone: stores.find((s) => s.id === r.storeId)?.timezone ?? DEFAULT_STORE_TIMEZONE })]}
                  name={r.formName}
                  subtitle={`v${r.versionNumber}${r.context === 'SHIFT_CLOSE' ? ' · Corte' : ''}`}
                  title={r.formName}
                />
              ))}
            </DenseList>
          ) : (
            <div className="data-table-wrap">
              <table className="data-table">
                <thead><tr><th>Formulario</th><th>Tienda</th><th>Estado</th><th>Actualizado</th><th></th></tr></thead>
                <tbody>
                  {recent.map((r) => (
                    <tr key={r.id}>
                      <td><strong>{r.formName}</strong> <small>v{r.versionNumber}{r.context === 'SHIFT_CLOSE' ? ' · Corte' : ''}</small></td>
                      <td>{r.storeName}</td>
                      <td><span className={`form-badge ${r.status === 'SUBMITTED' ? 'form-badge--active' : 'form-badge--draft'}`}>{r.status === 'SUBMITTED' ? 'Enviado' : 'Borrador'}</span></td>
                      <td>{new Date(r.submittedAt ?? r.startedAt).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short', timeZone: stores.find((s) => s.id === r.storeId)?.timezone ?? DEFAULT_STORE_TIMEZONE })}</td>
                      <td><div className="table-actions"><button className="text-button" onClick={() => setOpenId(r.id)} type="button">{r.status === 'SUBMITTED' ? 'Ver' : 'Continuar'}</button>{r.status !== 'SUBMITTED' && <button className="text-button text-button--danger" onClick={() => void discard(r.id)} type="button">Descartar</button>}</div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
